/** TanStack Query hooks: one per API resource. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type {
  Batch,
  CommitmentsView,
  LayoutDoc,
  Me,
  Member,
  Org,
  Programme,
  Project,
  ProjectCard,
  Run,
} from "./types";

export const keys = {
  me: ["me"] as const,
  projects: ["projects"] as const,
  project: (pid: string) => ["project", pid] as const,
  programme: (pid: string) => ["programme", pid] as const,
  layout: (pid: string) => ["layout", pid] as const,
  commitments: (pid: string, year: number) => ["commitments", pid, year] as const,
  batches: (pid: string) => ["batches", pid] as const,
  batch: (bid: string) => ["batch", bid] as const,
  runs: (pid: string) => ["runs", pid] as const,
  run: (rid: string) => ["run", rid] as const,
  tab: (rid: string, tab: string) => ["run", rid, "tab", tab] as const,
  chart: (rid: string, name: string, extra = "") => ["run", rid, "chart", name, extra] as const,
  members: (org: string) => ["members", org] as const,
  orgs: ["orgs"] as const,
  org: (org: string) => ["org", org] as const,
  deleted: ["deleted"] as const,
  audit: (org: string | null, day: string) => ["audit", org, day] as const,
  history: (pid: string, year: number) => ["history", pid, year] as const,
};

export const useMe = (enabled = true) => useQuery({ queryKey: keys.me, queryFn: () => api.get<Me>("/me"), enabled });
export const useProjects = () => useQuery({ queryKey: keys.projects, queryFn: () => api.get<ProjectCard[]>("/projects") });
export const useProject = (pid: string) =>
  useQuery({ queryKey: keys.project(pid), queryFn: () => api.get<Project>(`/projects/${pid}`) });
export const useProgramme = (pid: string) =>
  useQuery({ queryKey: keys.programme(pid), queryFn: () => api.get<Programme>(`/projects/${pid}/programme`) });
export const useLayout = (pid: string, enabled = true) =>
  useQuery({
    queryKey: keys.layout(pid),
    queryFn: () => api.get<LayoutDoc>(`/projects/${pid}/layout`),
    enabled,
    retry: false,
  });
export const useCommitments = (pid: string, year: number | null) =>
  useQuery({
    queryKey: keys.commitments(pid, year ?? -1),
    queryFn: () => api.get<CommitmentsView>(`/projects/${pid}/commitments/${year}`),
    enabled: year !== null,
  });
export const useBatches = (pid: string, enabled = true) =>
  useQuery({
    queryKey: keys.batches(pid),
    queryFn: () => api.get<{ id: string; files: number; processed: number | null; created_at: string }[]>(`/projects/${pid}/batches`),
    enabled,
  });
export const useBatch = (bid: string | undefined) =>
  useQuery({ queryKey: keys.batch(bid ?? ""), queryFn: () => api.get<Batch>(`/batches/${bid}`), enabled: !!bid });
export const useRuns = (pid: string, enabled = true) =>
  useQuery({
    queryKey: keys.runs(pid),
    queryFn: () =>
      api.get<
        { id: string; batch_id: string; reporting_year: number; status: string; created_at: string; saved_year: number | null; window_local: Record<string, string> }[]
      >(`/projects/${pid}/runs`),
    enabled,
  });
export const useRun = (rid: string) => useQuery({ queryKey: keys.run(rid), queryFn: () => api.get<Run>(`/runs/${rid}`) });
export function useTab<T>(rid: string, tab: string, enabled = true) {
  return useQuery({
    queryKey: keys.tab(rid, tab),
    queryFn: () => api.get<T>(`/runs/${rid}/${tab}`),
    enabled,
    staleTime: Infinity, // runs are immutable
  });
}
export function useChart(rid: string, path: string, enabled = true) {
  return useQuery({
    queryKey: keys.chart(rid, path),
    queryFn: () => api.get<{ data: any[]; layout: Record<string, any> }>(`/runs/${rid}/charts/${path}`),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}
export const useMembers = (org: string | null | undefined) =>
  useQuery({ queryKey: keys.members(org ?? ""), queryFn: () => api.get<Member[]>(`/orgs/${org}/members`), enabled: !!org });
export const useOrgs = (enabled = true) => useQuery({ queryKey: keys.orgs, queryFn: () => api.get<Org[]>("/admin/orgs"), enabled });
export const useOrg = (org: string | null | undefined) =>
  useQuery({ queryKey: keys.org(org ?? ""), queryFn: () => api.get<Org>(`/orgs/${org}`), enabled: !!org });
export const useDeletedProjects = (enabled = true) =>
  useQuery({
    queryKey: keys.deleted,
    queryFn: () =>
      api.get<{ project_id: string; org_id: string; label: string; deleted_by: string; deleted_at: string }[]>(
        "/admin/deleted-projects",
      ),
    enabled,
  });
export const useHistory = (pid: string, year: number, enabled = true) =>
  useQuery({
    queryKey: keys.history(pid, year),
    queryFn: () => api.get<Record<string, any>[]>(`/projects/${pid}/years/${year}/history`),
    enabled,
  });
export const useAudit = (org: string | null, day: string, enabled = true) =>
  useQuery({
    queryKey: keys.audit(org, day),
    queryFn: () => api.get<Record<string, any>[]>(org ? `/orgs/${org}/audit?day=${day}` : `/admin/audit?day=${day}`),
    enabled,
  });

/** Mutation that invalidates the given query keys on success. */
export function useApiMutation<TBody, TResult>(
  fn: (body: TBody) => Promise<TResult>,
  invalidate: readonly (readonly unknown[])[] = [],
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => client.invalidateQueries({ queryKey: key })));
    },
  });
}
